import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import DangerCircle from '@/images/DangerCircle.svg';

import styled from '@emotion/styled';
import { Skeleton, TextareaAutosize, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { TranslatedCaption } from '../../types';
import DurationErrorPopup from './DurationErrorPopup';
import PhraseTimestampsIndicator from './PhraseTimestampsIndicator';
import { getTextLengthRange } from './utils';

const ListItemContainer = styled.li`
    display: grid;
    grid-template-areas:
        'header header'
        'original translated';
    grid-template-columns: 1fr 1fr;
    grid-template-rows: 36px 1fr;
    column-gap: 4rem;
    margin-bottom: 1.2rem;
    border-bottom: 1px solid ${({ theme }) => theme.colors.black[300]};
`;

const CardHeader = styled.header`
    grid-area: header;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const TextContainer = styled.div`
    padding: 1.6rem 0;
`;

const OriginalTextContainer = styled(TextContainer)`
    grid-area: original;
`;

const TranslatedTextContainer = styled(TextContainer)`
    padding: 0.8rem 0;
    grid-area: translated;
`;

const TextInputWrapper = styled.div`
    position: relative;
    width: 100%;
`;

const DurationMismatchBadge = styled(Typography)`
    border-radius: 8px;
    padding: 0.4rem 1rem;
    line-height: 2rem;
    font-weight: 500;
    background-color: ${({ theme }) => theme.colors.error[50]};
    color: ${({ theme }) => theme.colors.error[750]};
    display: flex;
    align-items: center;
    gap: 8px;
    width: fit-content;
    position: absolute;
    top: -38px;
    left: 0;
    right: 0;
    margin-inline: auto;
`;

const TranslationTextInput = styled(TextareaAutosize, { shouldForwardProp: p => p !== 'isError' })<{ isError?: boolean }>`
    font-size: 1.4rem;
    line-height: 2.2rem;
    outline: none;
    border: none;
    width: 100%;
    resize: none;
    padding: 1rem;
    border-radius: 8px;
    caret-color: ${({ theme }) => theme.colors.primary[900]};
    background-color: ${({ theme, isError }) => (isError ? theme.colors.error[50] : 'transparent')};
    transition: background-color 0.2s;

    &:hover {
        background-color: ${({ theme, isError }) => (isError ? theme.colors.error[50] : theme.colors.black[100])};
        transition: background-color 0.2s;
    }
    ::selection {
        background: rgba(255, 136, 46, 0.15);
    }
`;

const StyledSkeleton = styled(Skeleton)`
    font-size: 1.4rem;
`;

interface IProps {
    caption: TranslatedCaption;
    index: number;
}

function CaptionListItem({ caption, index }: IProps) {
    const theme = useTheme();
    const { control, watch, setError, clearErrors, resetField } = useFormContext();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [mouseOnButton, setMouseOnButton] = useState(false);
    const [mouseOnPopup, setMouseOnPopup] = useState(false);
    const selectedTranslationId = watch('selectedTranslationId');
    const fieldName = `captionsByTranslationId.${selectedTranslationId}.sentences.${index}.text`;
    const { field, fieldState } = useController({
        control,
        name: fieldName,
    });
    const { value, onChange } = field;
    const defaultValueRef = useRef<string>(value);
    const { minLength, maxLength } = getTextLengthRange(defaultValueRef.current);

    const { start, end, originText } = caption;

    const lengthError = ['minLength', 'maxLength'].includes(fieldState.error?.type || '');

    useEffect(() => {
        if (value.length < minLength) {
            setError(fieldName, { type: 'minLength' });
        } else if (value.length > maxLength) {
            setError(fieldName, { type: 'maxLength' });
        } else if (fieldState.error) {
            clearErrors(fieldName);
        }
    }, [value, setError, fieldState.error]);

    function handleErrorMouseEnter(e: React.MouseEvent<HTMLDivElement>) {
        setAnchorEl(e.currentTarget);
        setMouseOnButton(true);
    }

    function handleErrorMouseLeave() {
        setMouseOnButton(false);
    }

    function handlePopupMouseEnter() {
        setMouseOnPopup(true);
    }

    function handlePopupMouseLeave() {
        setMouseOnPopup(false);
    }

    function handleReset() {
        setAnchorEl(null);
        setMouseOnPopup(false);
        setMouseOnButton(false);
        resetField(fieldName);
    }

    return (
        <ListItemContainer>
            <CardHeader>
                <PhraseTimestampsIndicator start={start} end={end}></PhraseTimestampsIndicator>
            </CardHeader>
            <OriginalTextContainer>
                <Typography variant="body2" lineHeight="2.2rem">
                    {originText}
                </Typography>
            </OriginalTextContainer>
            <TranslatedTextContainer>
                <TextInputWrapper>
                    {lengthError && (
                        <>
                            <DurationMismatchBadge
                                variant="body2"
                                onMouseEnter={handleErrorMouseEnter}
                                onMouseLeave={handleErrorMouseLeave}>
                                <MaskedIcon src={DangerCircle} size="16px" color={theme.colors.error[750]} />
                                Duration mismatch
                            </DurationMismatchBadge>
                            <DurationErrorPopup
                                mode={fieldState.error?.type === 'minLength' ? 'slow' : 'fast'}
                                anchorEl={anchorEl}
                                open={!!anchorEl && (mouseOnButton || mouseOnPopup)}
                                onMouseEnter={handlePopupMouseEnter}
                                onMouseLeave={handlePopupMouseLeave}
                                lengths={{ min: minLength, max: maxLength, current: value.length }}
                                onReset={handleReset}
                            />
                        </>
                    )}
                    <TranslationTextInput
                        className="translation-proofread__captions__listitem__textarea"
                        value={value}
                        onChange={onChange}
                        isError={lengthError}
                    />
                </TextInputWrapper>
            </TranslatedTextContainer>
        </ListItemContainer>
    );
}

function CaptionListItemSkeleton() {
    return (
        <ListItemContainer>
            <CardHeader>
                <StyledSkeleton variant="text" width={62} />
            </CardHeader>
            <OriginalTextContainer>
                <StyledSkeleton variant="text" width={254} />
                <StyledSkeleton variant="text" width={156} />
            </OriginalTextContainer>
            <TranslatedTextContainer>
                <StyledSkeleton variant="text" width={254} />
                <StyledSkeleton variant="text" width={156} />
            </TranslatedTextContainer>
        </ListItemContainer>
    );
}

export { CaptionListItemSkeleton };
export default CaptionListItem;
